import autobind from 'core-decorators/lib/autobind';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { WixConnect } from 'shared/utils/wix-connect';
import { isNeverShow } from 'widget/utils/video-list';
import { isSignInVisible } from 'shared/utils/layout-helpers';
import { mainItemIsChannelCover } from 'widget/utils/overlay-buttons-utils';

import { openSubscribeOverlay } from 'widget/redux/client/actions/mobile-overlay-openners/payment-pages-actions';
import { visitChannel } from 'widget/redux/client/actions/mobile-overlay-openners/main-openners';

import { getChannelForWidget } from 'shared/selectors/channels';
import { getCurrentSiteUser } from 'widget/selectors/current-site-user';
import {
  getAllSettings,
  canShowMainItemTitle,
  areElementsRounded,
  canShowVideoList,
  isCompactLayout,
  isStripLayout,
} from 'shared/selectors/app-settings';

import SubscribeShownBIOverlay from '../../bi/_subscribe-shown-bi-overlay';
import { withBiEvent } from 'shared/bi/send-bi-event';

import { PlayButton, PurchaseButton } from './buttons/middle-buttons';
import { BottomButton, SubscribeBottomButton } from './buttons/bottom-buttons';
import BaseOverlay from 'widget/components/player-overlay/_base-overlay';
import renderVideoPlayButtonText from 'widget/components/overlay-texts/video/play-button-text';
import renderVideoSignInToWatchButtonText from 'widget/components/overlay-texts/video/sign-in-to-watch-button-text';
import renderChannelPlayButtonText from 'widget/components/overlay-texts/channel/play-button-text';
import renderChannelSignInToWatchButtonText from 'widget/components/overlay-texts/channel/sign-in-to-watch-button-text';
import renderOpenVideoWithoutPreviewButtonText from 'widget/components/overlay-texts/channel/open-video-without-preview-button-text';
import renderPurchaseButtonText from 'widget/components/overlay-texts/purchase-button-text';
import renderChannelSubscribeButtonText, { isVisible as isChannelSubscribeButtonVisible } from 'widget/components/overlay-texts/channel/subscribe-button-text';
import ClampText from 'shared/components/clamp-text/clamp-text';

import styles from './three-rows-widget.styl';

import translations from './translations';

const WIDGET_PADDING = 30;
const MARGIN_BETWEEN_BOTTOM_BUTTONS = 20;
const BOTTOM_BUTTON_PADDING = 16;
const MAX_SINGLE_BUTTON_CONTENT_WIDTH = window.innerWidth - 2 * WIDGET_PADDING - 2 * BOTTOM_BUTTON_PADDING;
const MAX_TWO_BUTTONS_CONTENT_WIDTH = MAX_SINGLE_BUTTON_CONTENT_WIDTH - MARGIN_BETWEEN_BOTTOM_BUTTONS;


@connect(state => {
  const channelData = getChannelForWidget(state);
  const appSettings = getAllSettings(state);
  const isVideoListHiddenInStrip = isStripLayout(state) && isNeverShow(state.appSettings.numbers.videoListVisibility);
  const isVideoListHiddenInCompact = isCompactLayout(state) && !canShowVideoList(state);

  return {
    channelData,
    appSettings,
    isChannelCover: mainItemIsChannelCover(appSettings),
    isSignInVisible: isSignInVisible(appSettings),
    isVideoListHidden: isVideoListHiddenInStrip || isVideoListHiddenInCompact,
    canShowMainItemTitle: canShowMainItemTitle(state),
    isSingleVideoAtChannel: channelData.videosCount === 1,
    currentSiteUser: getCurrentSiteUser(state),
    isRounded: areElementsRounded(state),
  };
}, {
  onVisitChannelClick: visitChannel,
  onSubscribeClick: openSubscribeOverlay,
})
@WixConnect(styles)
export default class ThreeRowsWidgetOverlay extends SubscribeShownBIOverlay {

  static displayName = 'ThreeRowsWidgetOverlay';

  static propTypes = {
    ...BaseOverlay.propTypes,
    onSubscribeClick: React.PropTypes.func,
    onVisitChannelClick: React.PropTypes.func,
    onBuyVideoClick: React.PropTypes.func,
    onPlayClick: React.PropTypes.func,
    onOpenVideoClick: React.PropTypes.func,
    onSignInRequested: React.PropTypes.func.isRequired,
    bottomButtons: React.PropTypes.array,
    middleButton: React.PropTypes.object,

    isChannelCover: React.PropTypes.bool,
    isSignInVisible: React.PropTypes.bool,
    isVideoListHidden: React.PropTypes.bool,
    canShowMainItemTitle: React.PropTypes.bool,
    isSingleVideoAtChannel: React.PropTypes.bool,
    isRounded: React.PropTypes.bool,
  };

  static defaultProps = {
    onSubscribeClick: _.noop,
    onVisitChannelClick: _.noop,
  };

  get canOpenChannelPage() {
    const { isSingleVideoAtChannel } = this.props;

    return this.isChannelCoverView && !isSingleVideoAtChannel;
  }

  get title() {
    const { onOpenVideoClick, onVisitChannelClick, canShowMainItemTitle } = this.props;

    if (!canShowMainItemTitle) {
      return null;
    }

    const titleText = this.titleText;

    if (!titleText) {
      return null;
    }

    return (
      <span onClick={this.canOpenChannelPage ? onVisitChannelClick : onOpenVideoClick}
            styleName="title-text">
        <ClampText lineClamp={2}>
          {titleText}
        </ClampText>
      </span>
    );
  }

  get playButton() {
    const text = this.isChannelCoverView ?
      renderChannelPlayButtonText(this.buttonTextProps) :
      renderVideoPlayButtonText(this.buttonTextProps);

    if (!text) {
      return null;
    }

    return (
      <PlayButton onClick={this.props.onPlayClick}>
        {text}
      </PlayButton>
    );
  }

  get signInToWatchButton() {
    const text = this.isChannelCoverView ?
      renderChannelSignInToWatchButtonText(this.buttonTextProps) :
      renderVideoSignInToWatchButtonText(this.buttonTextProps);

    if (!text) {
      return null;
    }

    return (
      <PlayButton onClick={this.props.onPlayClick}>
        {text}
      </PlayButton>
    );
  }

  get openVideoButton() {
    if (!this.isChannelCoverView) {
      return null;
    }

    const text = renderOpenVideoWithoutPreviewButtonText(this.buttonTextProps);

    if (!text) {
      return null;
    }

    return (
      <PlayButton onClick={this.props.onOpenVideoClick}>
        {text}
      </PlayButton>
    );
  }

  get purchaseButton() {
    const text = renderPurchaseButtonText(this.buttonTextProps);

    if (!text) {
      return null;
    }

    return (
      <PurchaseButton onClick={this.props.onBuyVideoClick}>
        {text}
      </PurchaseButton>
    );
  }

  get signInButton() {
    const { isSignInVisible, currentSiteUser, onSignInRequested } = this.props;

    if (!isSignInVisible || currentSiteUser) {
      return null;
    }

    return (
      <BottomButton styleName="bottom-button"
                    dataHook="mobileSignInButton"
                    key="sign-in-button"
                    onClick={onSignInRequested}>
        <FormattedMessage {...translations.signIn} />
      </BottomButton>
    );
  }

  @autobind
  @withBiEvent('widget.exploreThisChannel.clicked')
  handleVisitChannelClick() {
    this.props.onVisitChannelClick();
  }

  get visitChannelButton() {
    return (
      <BottomButton styleName="bottom-button"
                    key="visit-channel-button"
                    onClick={this.handleVisitChannelClick}>
        <FormattedMessage {...translations.viewChannel} />
      </BottomButton>
    );
  }

  renderSubscribeButton(hasButtonBeside = false) {
    const text = renderChannelSubscribeButtonText(this.buttonTextProps);

    if (!text) {
      return null;
    }

    const styleName = classnames('bottom-button');
    const maxWidth = hasButtonBeside ? MAX_TWO_BUTTONS_CONTENT_WIDTH : MAX_SINGLE_BUTTON_CONTENT_WIDTH;

    return (
      <SubscribeBottomButton styleName={styleName}
                             key="subscribe-button"
                             forceShortText={hasButtonBeside}
                             maxWidth={maxWidth}
                             onClick={this.props.onSubscribeClick}>
        {text}
      </SubscribeBottomButton>
    );
  }

  get bottomButtons() {
    const _isChannelSubscribeButtonVisible = isChannelSubscribeButtonVisible(this.buttonTextProps);
    const { isVideoListHidden, isSingleVideoAtChannel } = this.props;

    if (isVideoListHidden || isSingleVideoAtChannel) {
      const signInButton = this.signInButton;

      if (!signInButton && !_isChannelSubscribeButtonVisible) {
        return [];
      }

      return _.compact([
        signInButton,
        this.renderSubscribeButton(Boolean(signInButton)),
      ]);
    }

    const visitChannelButton = this.visitChannelButton;

    if (!visitChannelButton && !_isChannelSubscribeButtonVisible) {
      return [];
    }

    return _.compact([
      visitChannelButton,
      this.renderSubscribeButton(Boolean(visitChannelButton)),
    ]);
  }

  get bottomButtonsBlock() {
    const bottomButtons = this.bottomButtons;

    if (_.isEmpty(bottomButtons)) {
      return null;
    }

    const { isRounded } = this.props;

    const styleNames = classnames('buttons', {
      single: bottomButtons.length === 1,
      rounded: isRounded,
    });

    return (
      <div styleName={styleNames}>
        {bottomButtons}
      </div>
    );
  }

  render() {
    const { className } = this.props;

    return (
      <div styleName="overlay"
           className={className}>

        <div styleName="main">
          <div styleName="title">
            {this.title}
          </div>

          <div styleName="play-container">
            {this.playButton || this.signInToWatchButton || this.openVideoButton || this.purchaseButton}
          </div>
        </div>

        {this.bottomButtonsBlock}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/components/player-overlay/mobile/three-rows-widget/three-rows-widget.js